var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export const objectFromEntries = Object.fromEntries;
export const objectEntries = Object.entries;
export const objectKeys = Object.keys;
export const objectValues = Object.values;
export const objectIsEmpty = (obj) => objectValues(obj).filter((v) => v !== undefined && v !== null).length === 0;
/**
 * This is just a way to get TypeBox Static<> types to show up better in type inference. Ignore this type.
 */
class FixTsMappedTypes {
}
export const firstNameLastNameFromName = (name) => {
    // This isn't very exact at all as firstnames can have spaces in them
    // so this is just a best guess
    const parts = name.split(' ');
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
};
export const createRandomId = () => {
    var _a;
    if ((_a = globalThis.crypto) === null || _a === void 0 ? void 0 : _a.randomUUID) {
        return globalThis.crypto.randomUUID();
    }
    return `${numberToString(Math.random())}-${numberToString(Math.random())}-${numberToString(Date.now())}`;
};
const numberToString = (n) => n.toString(36).substring(2);
export const assertUnreachable = (_) => {
    throw new Error('Unreachable code reached');
};
const isLocalStorageAvailable = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return false;
    }
    try {
        const testKey = '__test__';
        window.localStorage.setItem(testKey, testKey);
        window.localStorage.removeItem(testKey);
        return true;
    }
    catch (_a) {
        return false;
    }
};
export const withLocalStorageCache = (cacheKey, expiryMs, fetchFn) => __awaiter(void 0, void 0, void 0, function* () {
    let cached;
    if (isLocalStorageAvailable()) {
        try {
            const cachedData = window.localStorage.getItem(cacheKey);
            if (cachedData) {
                cached = JSON.parse(cachedData);
                const now = Date.now();
                if (cached.timestamp && now - cached.timestamp < expiryMs) {
                    return cached.data;
                }
            }
        }
        catch (error) {
            console.warn(`Error reading cache from localStorage (${cacheKey}):`, error);
        }
    }
    const fresh = yield fetchFn();
    if (isLocalStorageAvailable()) {
        try {
            const cacheData = { data: fresh, timestamp: Date.now() };
            window.localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        }
        catch (error) {
            console.warn(`Error saving data to localStorage (${cacheKey}):`, error);
        }
    }
    return fresh;
});
export const createMemoizedFunction = (fetchFn) => {
    let promise;
    return () => {
        if (promise === undefined) {
            promise = fetchFn();
        }
        return promise;
    };
};
/**
 * Creates a memoized function that can handle parameters and prevents both concurrent and sequential duplicate requests.
 *
 * This function implements a sophisticated two-tier caching system:
 *
 * 1. **Promise Cache (promiseCache)**: Stores pending promises to prevent concurrent duplicate requests
 *    - When multiple calls are made with the same parameters while a request is in flight,
 *      they all receive the same promise instead of triggering multiple network requests
 *
 * 2. **Result Cache (resultCache)**: Stores completed results to prevent sequential duplicate requests
 *    - Once a request completes successfully, the result is cached permanently
 *    - Future calls with the same parameters return the cached result immediately (no network request)
 *
 * Flow example:
 * - Call 1: Creates promise, stores in promiseCache, starts network request
 * - Call 2 (concurrent): Returns same promise from promiseCache
 * - Promise completes: Result moves to resultCache, promise removed from promiseCache
 * - Call 3 (sequential): Returns cached result from resultCache immediately
 *
 * Failed requests are NOT cached, allowing for retries.
 *
 * @param fetchFn - The async function to memoize that takes parameters
 * @param createCacheKey - Optional custom cache key generator. Defaults to JSON.stringify(args)
 * @returns A memoized version of the function that prevents duplicate requests
 */
export const createParameterizedMemoizedFunction = (fetchFn, createCacheKey) => {
    const promiseCache = new Map();
    const resultCache = new Map();
    return (...args) => {
        const cacheKey = createCacheKey ? createCacheKey(...args) : JSON.stringify(args);
        // Check if we have a cached result first
        if (resultCache.has(cacheKey)) {
            return Promise.resolve(resultCache.get(cacheKey));
        }
        // Check if we have a pending promise
        if (promiseCache.has(cacheKey)) {
            return promiseCache.get(cacheKey);
        }
        const promise = fetchFn(...args);
        promiseCache.set(cacheKey, promise);
        // When promise resolves, cache the result and clean up the promise cache
        promise.then((result) => {
            resultCache.set(cacheKey, result);
            promiseCache.delete(cacheKey);
            return result;
        }, (error) => {
            promiseCache.delete(cacheKey);
            throw error;
        });
        return promise;
    };
};
/**
 * Compares two SDK version strings (e.g., "1.2.3" and "1.3.0").
 *
 * Returns:
 * - 1 if v1 > v2
 * - -1 if v1 < v2
 * - 0 if v1 == v2
 * - null if either version string is invalid
 *
 * Handles versions with different lengths (e.g., "1.2" vs "1.2.0").
 */
export const compareSdkVersions = (v1, v2) => {
    var _a, _b;
    const v1Parts = v1.split('.').map((x) => Number.parseFloat(x));
    const v2Parts = v2.split('.').map((x) => Number.parseFloat(x));
    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
        if (Number.isNaN(v1Parts[i]) || Number.isNaN(v2Parts[i])) {
            return null;
        }
        const part1 = (_a = v1Parts[i]) !== null && _a !== void 0 ? _a : 0;
        const part2 = (_b = v2Parts[i]) !== null && _b !== void 0 ? _b : 0;
        if (part1 > part2)
            return 1;
        if (part1 < part2)
            return -1;
    }
    return 0;
};
//# sourceMappingURL=utils.js.map